import { PlusOutlined } from '@ant-design/icons'

import { Button, PageHeader } from '@lmc/ui-kit'
import useResidentFullName from 'hooks/useResidentFullName'
import { NAMESPACE } from 'locale'
import { procedureConstants } from 'locale/procedures'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useAbilityAccess from '../../../../components/AccessControl/useAbilityAccess'
import { ProcedureList } from './components/ProcedureList'
import ProcedureModal from './components/ProcedureModal'
import { useProceduresList } from './hooks/useProceduresList'
import type { Procedure } from './types'

export const ResidentProceduresScreen: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.PROCEDURES)
  const { id: residentId } = useParams<{ id: string }>()
  const residentName = useResidentFullName(residentId)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedProcedure, setSelectedProcedure] = useState<
    Procedure | undefined
  >(undefined)

  const {
    procedures,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useProceduresList({
    residentId: residentId || '',
    limit: 50
  })

  const hasAddAccess = useAbilityAccess([
    {
      i: 'create',
      this: 'Procedure'
    }
  ])

  const handleAddClick = () => {
    setSelectedProcedure(undefined)
    setIsModalVisible(true)
  }

  const handleEdit = (procedure: Procedure) => {
    setSelectedProcedure(procedure)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedProcedure(undefined)
  }

  const handleSuccess = () => {
    refetch()
  }

  return (
    <>
      <PageHeader
        title={
          residentName
            ? t(procedureConstants.PROCEDURES_FOR, { name: residentName })
            : t(procedureConstants.PROCEDURES)
        }
        help={{
          content: t(procedureConstants.PROCEDURES_TOOLTIP)
        }}
        sticky
      >
        {hasAddAccess && (
          <Button variant="primary" onClick={handleAddClick}>
            <PlusOutlined />
            {t(procedureConstants.ADD_PROCEDURE)}
          </Button>
        )}
      </PageHeader>

      <ProcedureList
        procedures={procedures}
        isLoading={isLoading}
        onEdit={handleEdit}
        onRefetch={refetch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      <ProcedureModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        procedure={selectedProcedure}
        residentId={residentId || ''}
      />
    </>
  )
}
